import React, { useState, useEffect, useCallback } from "react";
import {
  ActionList,
  Box,
  IconButton,
  Text,
  Truncate,
  ProgressBar,
} from "@primer/react";
// Whatever sits here has to stay distinct from the spinning SyncIcon that
// StatusIndicator overlays on this same button while work is running.
import { InboxIcon } from "@primer/octicons-react";
import railsFetch from "../railsFetch";
import { BulkOperation } from "../BulkActions/types";
import {
  useTrayDropdown,
  StatusIcon,
  StatusIndicator,
  TrayDropdown,
  ViewAllLink,
  formatShortDateTime,
  isExpired,
  isActive,
  isNewComplete,
} from "../TrayShared";

export interface OperationsTrayProps {
  eventId: string;
  bulkOperationsUrl: string;
  // GR-2855: the org-level mount on the events index has no operations page to
  // link to, so the "View All" row is omitted there rather than pointing
  // nowhere.
  operationsPageUrl?: string;
}

// Keep in sync with OperationsGrid::OperationLabelColumn::ACTION_LABELS —
// an action key missing here renders raw ("Bulk restore / Registration").
const ACTION_LABELS: Record<string, string> = {
  confirm: "Confirm",
  cancel: "Cancel",
  delete: "Delete",
  resend_confirmation: "Resend Confirmation",
  restore: "Restore",
  send: "Send / Resend",
};

// GR-2855: a clone acts on one event rather than N records, so the
// "Bulk <action> / <resource>" shape reads wrong for it.
const CLONE_RESOURCE_TYPE = "event_clone";

const getOperationLabel = (op: BulkOperation): string => {
  if (op.resourceType === CLONE_RESOURCE_TYPE) {
    return op.clonedEventTitle ? `Cloned ${op.clonedEventTitle}` : "Cloning event";
  }

  const action = ACTION_LABELS[op.actionKey] || op.actionKey;
  return `Bulk ${action} / ${op.resourceType}`;
};

export const OperationsTray: React.FC<OperationsTrayProps> = ({
  eventId,
  bulkOperationsUrl,
  operationsPageUrl,
}) => {
  const [operations, setOperations] = useState<BulkOperation[]>([]);
  const [hasActiveOperations, setHasActiveOperations] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { isOpen, setIsOpen, menuRef, buttonRef } = useTrayDropdown();

  const fetchOperations = useCallback(async () => {
    try {
      const response = await railsFetch(
        `${bulkOperationsUrl}.json`,
        { method: "GET", headers: { Accept: "application/json" } }
      );

      const data: BulkOperation[] = await response.json();
      const validOps = data.filter((op) => !isExpired(op));

      setOperations(validOps);
      setHasActiveOperations(validOps.some((op) => isActive(op)));
      setHasLoaded(true);
    } catch (error) {
      console.error("[OperationsTray] Failed to fetch bulk operations:", error);
      setHasLoaded(true);
    }
  }, [bulkOperationsUrl]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  // Poll for status updates when there are active operations
  useEffect(() => {
    if (!hasActiveOperations) return;

    const interval = setInterval(fetchOperations, 3000);
    return () => clearInterval(interval);
  }, [hasActiveOperations, fetchOperations]);

  // Listen for bulk operation events
  useEffect(() => {
    const handleOperationStarted = () => fetchOperations();
    const handleOperationUpdated = () => fetchOperations();

    window.addEventListener("bulkOperationStarted", handleOperationStarted);
    window.addEventListener("bulkOperationUpdated", handleOperationUpdated);
    return () => {
      window.removeEventListener("bulkOperationStarted", handleOperationStarted);
      window.removeEventListener("bulkOperationUpdated", handleOperationUpdated);
    };
  }, [fetchOperations]);

  const hasOperations = hasLoaded && operations.length > 0;
  const hasProcessing = operations.some((op) => op.status === "processing");
  const hasNewComplete = operations.some((op) => isNewComplete(op));

  // GR-2482: the icon used to be gated on `hasOperations`, inherited from the
  // download tray where an expired export genuinely has nothing behind it.
  // Bulk operations are different: the rows are kept for RETENTION_DAYS (90)
  // and the Operations page is the only way to reach that history, but the
  // tray filters on the 6-hour `expiresAt`. So by the next day the list was
  // empty, the icon vanished, and the page — along with the "View All
  // Operations" link inside the dropdown — became unreachable entirely.
  //
  // The icon is now always rendered. The server already gates the whole tray
  // on the organisation's bulk_actions_enabled? flag, which is the correct
  // place for that decision; nothing here should hide it again.
  return (
    <>
      <Box sx={{ position: "relative", display: "inline-block" }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <IconButton
              ref={buttonRef}
              icon={InboxIcon}
              aria-label="Operations"
              aria-expanded={isOpen}
              aria-haspopup="menu"
              size="small"
              variant="invisible"
              onClick={() => setIsOpen(!isOpen)}
            />
            <StatusIndicator
              hasProcessing={hasProcessing}
              hasNewComplete={hasNewComplete}
            />
          </Box>

          {isOpen && (
            <TrayDropdown ref={menuRef}>
              <ActionList>
                <ActionList.Group title="Operations">
                  {!hasOperations && (
                    <ActionList.Item disabled>
                      <Text sx={{ fontSize: 0, color: "fg.muted" }}>
                        {hasLoaded ? "No recent operations" : "Loading…"}
                      </Text>
                    </ActionList.Item>
                  )}
                  {operations.map((op) => {
                    const isComplete = op.status === "complete";
                    const isFailed = op.status === "failed";
                    const isProcessing = op.status === "processing";
                    const isClone = op.resourceType === CLONE_RESOURCE_TYPE;
                    // succeededCount counts EventCloneJob::POST_STEPS, so a
                    // number here means internal stages ran, not things the
                    // organiser recognises. What they need to know is whether
                    // anything failed to come across.
                    const issueCount = op.cloneIssueCount ?? 0;

                    // A finished clone has an event behind it, and opening
                    // that event is the whole point of having watched the
                    // operation. Every other row stays inert: a bulk action
                    // spans many records, so there is nothing single to open.
                    // Only once the job has finished. The clone's id is recorded
                    // after the first of four steps, so linking earlier opened an
                    // event with unmapped dates and an unconverted site, and wrapped
                    // a live progress bar in an anchor.
                    const href = isComplete ? op.clonedEventUrl : undefined;

                    // An array rather than a fragment. ActionList finds its
                    // LeadingVisual by inspecting its children, and a fragment
                    // hides them behind one node, which drops the icon out of
                    // the slot and stacks it above the label.
                    const body = [
                      (
                        <ActionList.LeadingVisual key="visual">
                          <StatusIcon
                            status={op.status}
                            isNew={isNewComplete(op)}
                            hasIssues={isClone && issueCount > 0}
                          />
                        </ActionList.LeadingVisual>
                      ),
                      (
                        <Box key="detail" sx={{ flex: 0, minWidth: 0 }}>
                          <Truncate title={getOperationLabel(op)} maxWidth="100%" sx={{ fontSize: 0 }}>
                            {getOperationLabel(op)}
                          </Truncate>
                          {isProcessing && op.totalCount > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <ProgressBar progress={op.progressPercentage} sx={{ width: "100%" }} />
                              <Text sx={{ fontSize: "10px", color: "fg.subtle", display: "block", mt: "2px" }}>
                                {op.processedCount}/{op.totalCount} ({op.progressPercentage}%)
                              </Text>
                            </Box>
                          )}
                          {op.status === "pending" && (
                            <Text sx={{ fontSize: "10px", color: "fg.subtle", display: "block" }}>
                              Queued... {formatShortDateTime(op.createdAt, op.timezone)}
                            </Text>
                          )}
                          {isComplete && !isClone && (
                            <Text sx={{ fontSize: "10px", color: "fg.muted", display: "block" }}>
                              {op.succeededCount} succeeded{op.failedCount > 0 ? `, ${op.failedCount} failed` : ""}
                            </Text>
                          )}
                          {isComplete && isClone && issueCount > 0 && op.cloneIssueSummary && (
                            <Truncate
                              title={op.cloneIssueSummary}
                              maxWidth="100%"
                              style={{ fontSize: "10px", color: "var(--fgColor-attention, #9a6700)", display: "block" }}
                            >
                              {op.cloneIssueSummary}
                            </Truncate>
                          )}
                          {isFailed && op.errorMessage && (
                            <Truncate
                              title={op.errorMessage}
                              maxWidth="100%"
                              style={{ fontSize: "10px", color: "var(--fgColor-danger, #d1242f)", display: "block" }}
                            >
                              {op.errorMessage}
                            </Truncate>
                          )}
                        </Box>
                      ),
                    ];

                    return href ? (
                      <ActionList.LinkItem key={`op-${op.id}`} href={href}>
                        {body}
                      </ActionList.LinkItem>
                    ) : (
                      <ActionList.Item
                        key={`op-${op.id}`}
                        disabled
                        inactiveText={isComplete && !isClone
                          ? `${op.succeededCount} succeeded, ${op.failedCount} failed`
                          : undefined}
                      >
                        {body}
                      </ActionList.Item>
                    );
                  })}
                </ActionList.Group>
                {operationsPageUrl && (
                  <>
                    <ActionList.Divider />
                    <ViewAllLink href={operationsPageUrl} label="View All Operations" />
                  </>
                )}
              </ActionList>
            </TrayDropdown>
          )}
        </Box>
    </>
  );
};

export default OperationsTray;
